import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import portal, { ClaseDelAlumno, MisClasesDTO } from "../../../services/v2/portalAlumno.service";
import { abrirAulaEnPestana, mensajeDeAula } from "../../../utils/abrirAula";
import { Dinero, ListaDeReportes } from "./portalComun";
import "./portal.css";

/*
	Mis Clases (portal del alumno): clases de hoy y de los próximos 7 días desde sus matrículas vigentes,
	saldo vencido y su estado de cuenta resumido. Lo vencido va en oro, nunca en rojo.
	Fase 2 (aula virtual): "Entrar A Clase" solo en horarios con aula (plantel Online) y dentro de la ventana
	(10 min antes hasta el fin); antes dice "Abre a las 1:50 pm"; con saldo vencido va apagado y las próximas
	salen "Bloqueada". Un pago en revisión no desbloquea: solo el pago aplicado o un pase de 48 h. El
	servidor vuelve a verificar todo al entrar; esto solo evita ofrecer un botón que no va a funcionar.
*/
// Course name as stored (same as the class portal hero) with the period under it (Esteban, 2026-10-20: the module name is not shown).
const titulo = (c: { course: string }) => c.course

const TarjetaDeHoy: React.FC<{ c: ClaseDelAlumno; bloqueada: boolean; ahoraMs: number; pase: string | null }> = ({ c, bloqueada, ahoraMs, pase }) => {
	const navigate = useNavigate()
	const [entrando, setEntrando] = useState<boolean>(false)
	const [aviso, setAviso] = useState<string | null>(null)
	const aula = c.hasAula ? c.aula : null
	const abierta = aula !== null && ahoraMs >= aula.opensAtMs && ahoraMs <= aula.closesAtMs
	const porAbrir = aula !== null && ahoraMs < aula.opensAtMs

	const entrar = async () => {
		setAviso(null)
		setEntrando(true)
		try { await abrirAulaEnPestana(async () => (await portal.joinClass(c.scheduleId)).data.url) }
		catch (e) { setAviso(mensajeDeAula(e)) }
		finally { setEntrando(false) }
	}

	return (
		<div className="ptTarjeta ptHoy">
			<div className="ptEtiquetaFila">
				{c.state === "live" ? <><span className="ptEtiqueta ptEnCurso">En Curso</span><span className="ptTenue">Empezó hace {c.minutesSinceStart} min</span></>
					: c.state === "finished" ? <span className="ptEtiqueta ptTenue">Terminó</span>
						: <span className="ptEtiqueta ptEnCurso">Hoy</span>}
			</div>
			<div className="ptAulaFila">
				<div className="ptAulaTextos">
					<div className="ptTituloClase">{titulo(c)}</div>
					{c.period ? <div className="ptPeriodo">{c.period}</div> : null}
					<div className="ptTenue ptLinea">{[c.startLabel + " a " + c.endLabel, c.mentor].filter(Boolean).join(" · ")}</div>
				</div>
				{abierta || porAbrir ? (
					<button type="button" className="ptBoton ptBotonAula" disabled={bloqueada || porAbrir || entrando} onClick={entrar}>
						{porAbrir && !bloqueada ? "Abre a las " + aula!.opensLabel : "Entrar A Clase"}
					</button>
				) : null}
			</div>
			<button type="button" className="ptEnlace" onClick={() => navigate("/miClase?h=" + c.scheduleId)}>
				Abrir Mi Clase{c.lmsNew ? <span className="ptPuntoNuevo" role="img" aria-label="Hay novedades" /> : null}
			</button>
			{(abierta || porAbrir) && bloqueada ? <div className="ptAulaNota ptOro">Tienes un saldo vencido. Reporta tu pago o pide un pase en Caja.</div> : null}
			{(abierta || porAbrir) && !bloqueada && pase ? <div className="ptAulaNota ptTenue">Pase hasta el {pase}</div> : null}
			{aviso ? <div className="ptAulaNota ptOro" role="alert">{aviso}</div> : null}
		</div>
	)
}

const MisClases: React.FC = () => {
	const [datos, setDatos] = useState<MisClasesDTO | null>(null)
	const [error, setError] = useState<boolean>(false)
	const [verReportes, setVerReportes] = useState<boolean>(false)
	const [ahoraMs, setAhoraMs] = useState<number>(Date.now())
	const [desfase, setDesfase] = useState<number>(0)   // reloj del servidor - reloj de este aparato
	const navigate = useNavigate()

	useEffect(() => {
		let vivo = true
		portal.classes().then((r) => {
			if (!vivo) return
			setDesfase(r.data.serverNowMs ? r.data.serverNowMs - Date.now() : 0)
			setDatos(r.data)
		}).catch(() => { if (vivo) setError(true) })
		//Cada 20 s se revisa la ventana: el botón se enciende solo cuando abre el aula.
		const reloj = window.setInterval(() => setAhoraMs(Date.now()), 20000)
		return () => { vivo = false; window.clearInterval(reloj) }
	}, [])

	if (error) return <div className="pt"><div className="ptVacio">No Pudimos Cargar Tus Clases</div></div>
	if (datos === null) return <div className="pt" />

	const { classes, account, attendance, reports } = datos
	return (
		<div className="pt">
			<div className="ptPagina">
				<div className="ptColumna">
					<h1 className="ptH1">Hoy</h1>

					{account.hasOverdue ? (
						<div className="ptTarjeta ptVencido">
							<span className="ptEtiqueta ptOro">Saldo Vencido</span>
							<div className="ptVencidoFila">
								<div className="ptDato">
									<span className="ptTenue ptChico">{account.oldestOverdue?.title}</span>
									<span className="ptMonto ptOro">{account.overdue.map((m, i) => <div key={m.currency}><Dinero m={m} /></div>)}</span>
								</div>
								<div className="ptDato">
									<span className="ptTenue ptChico">Venció</span>
									<span className="ptFecha">{account.oldestOverdue?.dueLabel}</span>
								</div>
							</div>
							<div className="ptAcciones">
								<button type="button" className="ptBoton" onClick={() => navigate("/reportarPago")}>Reportar Mi Pago</button>
								<button type="button" className="ptEnlace" aria-expanded={verReportes} onClick={() => setVerReportes(!verReportes)}>Ya Lo Reporté</button>
							</div>
							{verReportes ? <ListaDeReportes reportes={reports} /> : null}
						</div>
					) : null}

					{classes.today.length === 0
						? <div className="ptTarjeta ptHoy"><div className="ptTituloClase">Hoy No Tienes Clases</div></div>
						: classes.today.map((c) => <TarjetaDeHoy key={c.enrollmentId + "-" + c.startLabel} c={c} bloqueada={account.blocksAula}
							ahoraMs={ahoraMs + desfase} pase={account.pass ? account.pass.untilLabel : null} />)}

					<h2 className="ptH2">Esta Semana</h2>
					{classes.week.length === 0
						? <div className="ptVacio">Sin Clases Esta Semana</div>
						: <div className="ptLista">
							{classes.week.map((c) => (
								<div className="ptFila" key={c.date + c.startLabel + c.enrollmentId}>
									<div className="ptFilaDia">
										<span className="ptFilaDiaNombre">{c.dayName} {c.dayNumber}</span>
										<span className="ptTenue ptChico">{c.startLabel} a {c.endLabel}</span>
									</div>
									<div className="ptFilaCurso">
										<span className="ptFilaTitulo">{titulo(c)}</span>
										{c.period ? <span className="ptPeriodo ptChico">{c.period}</span> : null}
										{c.mentor ? <span className="ptTenue ptChico">{c.mentor}</span> : null}
									</div>
									{c.hasAula && account.blocksAula ? <span className="ptBloqueada">Bloqueada</span> : null}
								</div>
							))}
						</div>}
				</div>

				<aside className="ptLateral">
					{classes.mine && classes.mine.length > 0 ? (
						<div className="ptTarjeta">
							<div className="ptTenue ptSubtitulo">Mis Cursos</div>
							{classes.mine.map((m) => (
								<button type="button" className="ptCurso" key={m.scheduleId} onClick={() => navigate("/miClase?h=" + m.scheduleId)}>
									<span className="ptCursoTextos"><span className="ptFilaTitulo">{titulo(m)}</span>{m.period ? <span className="ptPeriodo ptChico">{m.period}</span> : null}{m.mentor ? <span className="ptTenue ptChico">{m.mentor}</span> : null}</span>
									{m.lmsNew ? <span className="ptPuntoNuevo" role="img" aria-label="Hay novedades" /> : null}
								</button>
							))}
						</div>
					) : null}
					<div className="ptTarjeta">
						<div className="ptTenue ptSubtitulo">Mi Cuenta</div>
						<div className="ptDato">
							<span className="ptTenue ptChico">{account.hasOverdue ? "Saldo Vencido" : "Mensualidades"}</span>
							{account.hasOverdue
								? <span className="ptMontoGrande ptOro">{account.overdue.map((m) => <div key={m.currency}><Dinero m={m} /></div>)}</span>
								: <span className="ptMontoGrande">Al Corriente</span>}
						</div>
						{account.nextDueLabel ? <><div className="ptRegla" /><div className="ptPar"><span className="ptTenue">Próximo Pago</span><b>{account.nextDueLabel}</b></div></> : null}
						<Link className="ptEnlace" to="/statement">Ver Mi Estado De Cuenta</Link>
					</div>
					<div className="ptTarjeta">
						<div className="ptTenue ptSubtitulo">Mi Asistencia</div>
						<div className="ptAsistencia"><span className="ptAsistenciaNumero">{attendance.present}</span><span className="ptTenue">de {attendance.total}</span></div>
						<Link className="ptEnlace" to="/misAsistencias">Ver Mis Asistencias</Link>
					</div>
				</aside>
			</div>
		</div>
	)
}

export default MisClases
